import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

interface Props {
  open: boolean;
  username: string;
  onConfirm: (newPassword: string) => void;
  onCancel: () => void;
}

const ResetPasswordDialog = ({ open, username, onConfirm, onCancel }: Props) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  // 当对话框关闭时重置所有状态
  useEffect(() => {
    if (!open) {
      setPassword("");
      setConfirmPassword("");
      setError("");
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open]);

  const handleConfirm = () => {
    // 验证密码
    if (!password) {
      setError("请输入新密码");
      return;
    }
    
    if (password.length < 6) {
      setError("密码长度至少6位");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("两次输入的密码不一致");
      return;
    }
    
    setError("");
    onConfirm(password);
  };

  const handleCancel = () => {
    setPassword("");
    setConfirmPassword("");
    setError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    onCancel();
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="reset-password-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="reset-password-dialog-title">
        重置用户密码
      </DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: "16px" }}>
          <Alert severity="info">
            正在重置用户 <strong>{username}</strong> 的密码
          </Alert>
        </div>
        
        {error && (
          <div style={{ marginBottom: "16px" }}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}
        
        <FormControl fullWidth style={{ marginBottom: "16px" }}>
          <InputLabel htmlFor="new-password">新密码</InputLabel>
          <OutlinedInput
            id="new-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="新密码"
          />
        </FormControl>
        
        <FormControl fullWidth>
          <InputLabel htmlFor="confirm-password">确认新密码</InputLabel>
          <OutlinedInput
            id="confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowConfirmPassword}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="确认新密码"
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="outlined">
          取消
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          确认重置
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordDialog; 